import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import type { QueueLoginStatus, ScreenPopCall, ScreenPopResponse } from '../../types';
import {
  canAccessScreenPop,
  getStoredPhoneExtension,
  isExtensionConfirmedThisSession,
} from '../../utils/screenPopAccess';
import { isBookingQueueRole } from '../../utils/queueLoginRole';

const POLL_MS = 3000;
const QUEUE_STATUS_POLL_MS = 15000;

function isConnectedCall(call: ScreenPopCall): boolean {
  const state = String(call.state || '');
  const label = (call.state_label || '').trim();
  return state === '6' || label === 'در حال صحبت' || label.toLowerCase() === 'up';
}

const IncomingCallPop: React.FC = () => {
  const navigate = useNavigate();
  const [calls, setCalls] = useState<ScreenPopCall[]>([]);
  const [dismissed, setDismissed] = useState<Record<string, true>>({});
  const [expandedConnected, setExpandedConnected] = useState<Record<string, true>>({});
  const [listeningExt, setListeningExt] = useState(getStoredPhoneExtension());
  const [amiOk, setAmiOk] = useState<boolean | null>(null);
  const [dndActive, setDndActive] = useState(false);
  const [enteredIds, setEnteredIds] = useState<Record<string, true>>({});
  const [queueStatus, setQueueStatus] = useState<QueueLoginStatus | null>(null);
  const [queueBusy, setQueueBusy] = useState(false);
  const [queueError, setQueueError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const queueInFlight = useRef(false);
  const seenIds = useRef<Set<string>>(new Set());

  const enabled =
    canAccessScreenPop() &&
    isExtensionConfirmedThisSession() &&
    Boolean(getStoredPhoneExtension());

  const queueUiEnabled = enabled && isBookingQueueRole();

  const pollQueueStatus = useCallback(async () => {
    if (!queueUiEnabled || queueInFlight.current) return;
    queueInFlight.current = true;
    try {
      const { data } = await adminAPI.getQueueLoginStatus();
      setQueueStatus(data);
      setQueueError(null);
    } catch {
      // فیچر ممکن است برای این کاربر بی‌ربط باشد
    } finally {
      queueInFlight.current = false;
    }
  }, [queueUiEnabled]);

  const poll = useCallback(async () => {
    if (!enabled || inFlight.current) return;
    inFlight.current = true;
    try {
      const { data } = await adminAPI.getAmiScreenPop();
      const payload = data as ScreenPopResponse;
      const nextCalls = payload.calls || [];
      setCalls(nextCalls);

      const fresh: Record<string, true> = {};
      for (const call of nextCalls) {
        const id = call.unique_id;
        if (!id) continue;
        if (!seenIds.current.has(id)) {
          seenIds.current.add(id);
          fresh[id] = true;
        }
      }
      // پاک کردن idهایی که دیگر در لیست نیستند
      const live = new Set(nextCalls.map((c) => c.unique_id).filter(Boolean));
      for (const id of Array.from(seenIds.current)) {
        if (!live.has(id)) seenIds.current.delete(id);
      }
      if (Object.keys(fresh).length > 0) {
        setEnteredIds((prev) => ({ ...prev, ...fresh }));
        window.setTimeout(() => {
          setEnteredIds((prev) => {
            const next = { ...prev };
            for (const id of Object.keys(fresh)) delete next[id];
            return next;
          });
        }, 900);
      }

      if (payload.extension) setListeningExt(payload.extension);
      setAmiOk(Boolean(payload.is_configured));
      setDndActive(Boolean(payload.dnd_active));
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setCalls([]);
        setAmiOk(false);
        setDndActive(false);
      } else {
        setAmiOk(false);
      }
    } finally {
      inFlight.current = false;
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setCalls([]);
      setAmiOk(null);
      setDndActive(false);
      setQueueStatus(null);
      setQueueError(null);
      seenIds.current.clear();
      return;
    }
    setListeningExt(getStoredPhoneExtension());
    poll();
    const id = window.setInterval(poll, POLL_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [enabled, poll]);

  useEffect(() => {
    if (!queueUiEnabled) {
      setQueueStatus(null);
      setQueueError(null);
      return;
    }
    pollQueueStatus();
    const queueId = window.setInterval(pollQueueStatus, QUEUE_STATUS_POLL_MS);
    return () => {
      window.clearInterval(queueId);
    };
  }, [queueUiEnabled, pollQueueStatus]);

  const handleRestToggle = async () => {
    if (!queueStatus?.feature_enabled || queueBusy) return;
    setQueueBusy(true);
    setQueueError(null);
    try {
      const extension = getStoredPhoneExtension() || undefined;
      if (queueStatus.in_queue && queueStatus.verified && !queueStatus.on_rest) {
        const { data } = await adminAPI.leaveQueueLogin(extension);
        setQueueStatus(data);
      } else {
        const { data } = await adminAPI.joinQueueLogin(extension);
        setQueueStatus(data);
        if (!data.in_queue || !data.verified) {
          setQueueError(data.message || 'افزودن به صف تأیید نشد');
        }
      }
    } catch (err: any) {
      setQueueError(err?.response?.data?.detail || 'خطا در تغییر وضعیت صف');
    } finally {
      setQueueBusy(false);
    }
  };

  const visible = calls.filter((c) => c.unique_id && !dismissed[c.unique_id]);
  const queueFeatureOn = queueUiEnabled && Boolean(queueStatus?.feature_enabled);
  // فقط با تأیید AMI «داخل صف» نمایش بده — Success دروغین کافی نیست
  const reallyInQueue = Boolean(queueStatus?.in_queue && queueStatus?.verified);
  const onRest = !reallyInQueue;

  if (!enabled) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed bottom-4 left-36 z-[90] flex w-[min(100%-10rem,360px)] flex-col gap-2 sm:left-48">
      <style>{`
        @keyframes screenPopEnter {
          0% {
            opacity: 0;
            transform: translateY(28px) scale(0.92);
            filter: blur(4px);
          }
          55% {
            opacity: 1;
            transform: translateY(-6px) scale(1.03);
            filter: blur(0);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: blur(0);
          }
        }
        @keyframes screenPopRingPulse {
          0%, 100% {
            box-shadow: 0 12px 28px rgba(15, 118, 110, 0.18), 0 0 0 0 rgba(13, 148, 136, 0.35);
          }
          50% {
            box-shadow: 0 16px 36px rgba(15, 118, 110, 0.28), 0 0 0 10px rgba(13, 148, 136, 0);
          }
        }
        @keyframes screenPopNumberBeat {
          0%, 100% { transform: scale(1); }
          40% { transform: scale(1.04); }
          60% { transform: scale(0.99); }
        }
        @keyframes screenPopCollapseIn {
          0% { opacity: 0; transform: translateY(12px) scale(0.96); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .screen-pop-enter {
          animation: screenPopEnter 0.75s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .screen-pop-ringing {
          animation: screenPopRingPulse 1.6s ease-in-out infinite;
        }
        .screen-pop-number-beat {
          animation: screenPopNumberBeat 1.2s ease-in-out infinite;
          transform-origin: right center;
        }
        .screen-pop-collapse {
          animation: screenPopCollapseIn 0.35s ease-out both;
        }
      `}</style>

      <div
        className="pointer-events-auto flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white/95 px-3 py-1.5 text-[11px] text-slate-600 shadow"
        style={{ direction: 'rtl' }}
      >
        <span>
          پاپ‌آپ فعال · داخلی{' '}
          <strong dir="ltr">{listeningExt || getStoredPhoneExtension()}</strong>
          <span className="mr-2 text-slate-400">
            ·{' '}
            {dndActive
              ? 'DND'
              : amiOk === false
                ? 'AMI قطع'
                : amiOk
                  ? 'آماده'
                  : 'در حال اتصال...'}
          </span>
        </span>

        {queueFeatureOn && (
          <>
            <span className="text-slate-300">|</span>
            <span className={onRest ? 'font-medium text-amber-700' : 'font-medium text-emerald-700'}>
              {onRest ? 'خروج از صف و استراحت' : 'داخل صف'}
            </span>
            <button
              type="button"
              onClick={handleRestToggle}
              disabled={queueBusy}
              className={`rounded-md px-2.5 py-1 text-[11px] font-semibold transition disabled:opacity-60 ${
                onRest
                  ? 'bg-emerald-700 text-white hover:bg-emerald-800'
                  : 'bg-amber-600 text-white hover:bg-amber-700'
              }`}
            >
              {queueBusy ? '...' : onRest ? 'بازگشت به صف' : 'استراحت'}
            </button>
          </>
        )}
      </div>

      {queueError && (
        <div
          className="pointer-events-auto rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-[11px] text-red-700"
          style={{ direction: 'rtl' }}
        >
          {queueError}
        </div>
      )}

      {visible.map((call) => {
        const name = call.patient_name || null;
        const connected = isConnectedCall(call);
        const forceOpen = Boolean(expandedConnected[call.unique_id]);
        const collapsed = connected && !forceOpen;
        const isEntering = Boolean(enteredIds[call.unique_id]);
        const sourceLabel =
          call.patient_source === 'local'
            ? 'سیستم نوبت'
            : call.patient_source === 'his'
              ? 'HIS'
              : null;

        if (collapsed) {
          return (
            <div
              key={call.unique_id}
              className={`pointer-events-auto flex items-center gap-2 rounded-xl border border-teal-200 bg-white/95 px-3 py-2 shadow-lg screen-pop-collapse ${
                isEntering ? 'screen-pop-enter' : ''
              }`}
              style={{ direction: 'rtl' }}
            >
              <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
              <button
                type="button"
                onClick={() =>
                  setExpandedConnected((prev) => ({ ...prev, [call.unique_id]: true }))
                }
                className="min-w-0 flex-1 text-right"
              >
                <div className="truncate text-sm font-bold text-gray-900" dir="ltr">
                  {call.caller_number || '—'}
                  {name ? (
                    <span className="mr-2 font-semibold text-teal-800" dir="rtl">
                      · {name}
                    </span>
                  ) : null}
                </div>
                <div className="text-[10px] text-gray-400">در حال صحبت — برای جزئیات کلیک کنید</div>
              </button>
              <button
                type="button"
                onClick={() =>
                  setDismissed((prev) => ({ ...prev, [call.unique_id]: true }))
                }
                className="shrink-0 rounded-md px-1.5 py-0.5 text-[11px] text-gray-500 hover:bg-gray-100"
              >
                بستن
              </button>
            </div>
          );
        }

        return (
          <div
            key={call.unique_id}
            className={`pointer-events-auto overflow-hidden rounded-2xl border border-teal-200 bg-white shadow-2xl ${
              isEntering ? 'screen-pop-enter' : ''
            } ${!connected ? 'screen-pop-ringing' : ''}`}
            style={{ direction: 'rtl' }}
          >
            <div className="flex items-center justify-between bg-gradient-to-l from-teal-800 to-teal-600 px-4 py-2.5 text-white">
              <div className="flex items-center gap-2 text-sm font-bold">
                <span className="relative flex h-2.5 w-2.5">
                  {!connected && (
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-300 opacity-75" />
                  )}
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-200" />
                </span>
                {connected ? 'در حال صحبت' : 'تماس ورودی'}
              </div>
              <div className="flex items-center gap-1">
                {connected && (
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedConnected((prev) => {
                        const next = { ...prev };
                        delete next[call.unique_id];
                        return next;
                      })
                    }
                    className="rounded-md px-2 py-0.5 text-xs hover:bg-white/15"
                  >
                    جمع کردن
                  </button>
                )}
                <button
                  type="button"
                  onClick={() =>
                    setDismissed((prev) => ({ ...prev, [call.unique_id]: true }))
                  }
                  className="rounded-md px-2 py-0.5 text-xs hover:bg-white/15"
                >
                  بستن
                </button>
              </div>
            </div>

            <div className="space-y-2 px-4 py-3">
              <div
                className={`text-2xl font-extrabold tracking-wide text-gray-900 ${
                  !connected ? 'screen-pop-number-beat' : ''
                }`}
                dir="ltr"
              >
                {call.caller_number || '—'}
              </div>

              {name ? (
                <div className="text-base font-bold text-teal-800">
                  {name}
                  {sourceLabel && (
                    <span className="mr-2 text-xs font-medium text-gray-500">
                      ({sourceLabel})
                    </span>
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-500">نام در دیتابیس یافت نشد</div>
              )}

              <div className="flex items-center justify-between text-[11px] text-gray-400">
                <span>{call.state_label || 'در حال تماس'}</span>
                {typeof call.duration_seconds === 'number' && call.duration_seconds > 0 && (
                  <span>{call.duration_seconds} ثانیه</span>
                )}
              </div>

              {call.patient?.last_appointment_id && (
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/admin/appointments/${call.patient!.last_appointment_id}`)
                  }
                  className="mt-1 w-full rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800"
                >
                  مشاهده آخرین نوبت
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default IncomingCallPop;
